import React from "react";
import { BulletListIcon, LinkIcon, NumberedListIcon } from "./icons";
import type { RichTextToolT } from "./types";
import * as S from "./styles";

export type InlineFormatT = "bold" | "italic" | "underline" | "strike";
export type ListFormatT = "bullet" | "ordered";

type RichTextToolbarPropsT = {
  tools: RichTextToolT[];
  activeFormats: Record<string, unknown>;
  linkOpen: boolean;
  onToggleInline: (format: InlineFormatT) => void;
  onToggleList: (list: ListFormatT) => void;
  onOpenLink: () => void;
  onClear: () => void;
};

const INLINE_TOOLS: { tool: InlineFormatT; title: string; glyph: React.ReactNode }[] = [
  { tool: "bold", title: "Bold", glyph: <strong>B</strong> },
  { tool: "italic", title: "Italic", glyph: <em>I</em> },
  { tool: "underline", title: "Underline", glyph: <u>U</u> },
  { tool: "strike", title: "Strikethrough", glyph: <s>S</s> },
];

const LIST_TOOLS: { tool: ListFormatT; title: string; icon: React.ReactNode }[] = [
  { tool: "bullet", title: "Bulleted list", icon: <BulletListIcon /> },
  { tool: "ordered", title: "Numbered list", icon: <NumberedListIcon /> },
];

const keepEditorSelection = (event: React.MouseEvent) => event.preventDefault();

const RichTextToolbar = ({
  tools,
  activeFormats,
  linkOpen,
  onToggleInline,
  onToggleList,
  onOpenLink,
  onClear,
}: RichTextToolbarPropsT) => {
  const inlineTools = INLINE_TOOLS.filter(({ tool }) => tools.includes(tool));
  const listTools = LIST_TOOLS.filter(({ tool }) => tools.includes(tool));

  return (
    <S.Toolbar role="toolbar" aria-label="Formatting">
      {inlineTools.map(({ tool, title, glyph }) => (
        <S.ToolButton
          key={tool}
          type="button"
          title={title}
          aria-label={title}
          aria-pressed={Boolean(activeFormats[tool])}
          active={Boolean(activeFormats[tool])}
          onMouseDown={keepEditorSelection}
          onClick={() => onToggleInline(tool)}
        >
          {glyph}
        </S.ToolButton>
      ))}
      {inlineTools.length > 0 && listTools.length > 0 && <S.ToolDivider />}
      {listTools.map(({ tool, title, icon }) => (
        <S.ToolButton
          key={tool}
          type="button"
          title={title}
          aria-label={title}
          aria-pressed={activeFormats.list === tool}
          active={activeFormats.list === tool}
          onMouseDown={keepEditorSelection}
          onClick={() => onToggleList(tool)}
        >
          {icon}
        </S.ToolButton>
      ))}
      {tools.includes("link") && (
        <>
          <S.ToolDivider />
          <S.LinkToolButton
            type="button"
            title="Insert link"
            aria-label="Insert link"
            aria-pressed={linkOpen}
            active={linkOpen}
            onMouseDown={keepEditorSelection}
            onClick={onOpenLink}
          >
            <LinkIcon />
            Link
          </S.LinkToolButton>
        </>
      )}
      {tools.includes("clear") && (
        <>
          <S.ToolSpacer />
          <S.ClearToolButton
            type="button"
            title="Remove formatting"
            aria-label="Remove formatting"
            onMouseDown={keepEditorSelection}
            onClick={onClear}
          >
            Clear
          </S.ClearToolButton>
        </>
      )}
    </S.Toolbar>
  );
};

export default RichTextToolbar;
